// components/LayerFeedbackOverlays.tsx
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';

interface LayerFeedbackOverlaysProps {
  layerId: string;
  ghostContent: React.ReactNode;
  disableGhostContent?: boolean;
}

const LayerFeedbackOverlays: React.FC<LayerFeedbackOverlaysProps> = React.memo(({
  layerId,
  ghostContent,
  disableGhostContent = false,
}) => {
  const layerType = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.type ?? undefined);
  const isImageLayer = layerType !== 'video' && layerType !== 'audio';
  const pulseToken = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.pulseToken ?? 0);
  const isVectorizing = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.isVectorizing === true);
  const isRasterizing = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.isRasterizing === true);
  const isAligning = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.isAligning === true);
  const isExtracting = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.isExtracting === true);
  const isHarmonizingUnchanged = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.isHarmonizingUnchanged === true);
  const isIntegrating = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.isIntegrating === true);
  const isProcessing = isVectorizing || isRasterizing || isAligning || isExtracting || isHarmonizingUnchanged || isIntegrating;
  const isScissorsHoverTarget = useKernelStore((state) => {
    if (state.activeTool !== 'scissors') return false;
    if (state.nativeLayerCutSession !== null) return false;
    if (!isImageLayer) return false;
    return state.scissorsHoverLayerId === layerId;
  });

  const pulseRef = useRef<HTMLDivElement | null>(null);
  const pulseAnimationRef = useRef<Animation | null>(null);
  const pulseRunIdRef = useRef(0);
  const processingPulseRef = useRef<HTMLDivElement | null>(null);
  const processingPulseAnimationRef = useRef<Animation | null>(null);
  const ghostRef = useRef<HTMLDivElement | null>(null);
  const ghostAnimationRef = useRef<Animation | null>(null);
  const ghostRunIdRef = useRef(0);

  const triggerGhostPulse = useCallback(() => {
    if (!isImageLayer) return;
    const ghostElement = ghostRef.current;
    if (!ghostElement) return;

    const runId = ++ghostRunIdRef.current;
    const previousAnimation = ghostAnimationRef.current;
    if (previousAnimation) {
      previousAnimation.onfinish = null;
      previousAnimation.oncancel = null;
      previousAnimation.cancel();
    }

    ghostElement.style.opacity = '0';
    ghostElement.style.filter = 'brightness(1) contrast(1)';

    const animation = ghostElement.animate(
      [
        { opacity: 0, filter: 'brightness(1) contrast(1)', offset: 0 },
        { opacity: 0.46, filter: 'brightness(1.28) contrast(1.12)', offset: 0.14 },
        { opacity: 0, filter: 'brightness(1) contrast(1)', offset: 1 }
      ],
      {
        duration: 980,
        easing: 'ease-out'
      }
    );

    ghostAnimationRef.current = animation;

    const teardown = () => {
      if (ghostRunIdRef.current !== runId) {
        return;
      }
      if (ghostAnimationRef.current !== animation) {
        return;
      }
      ghostAnimationRef.current = null;
      if (ghostRef.current) {
        ghostRef.current.style.opacity = '0';
        ghostRef.current.style.filter = 'brightness(1) contrast(1)';
      }
    };

    animation.onfinish = teardown;
    animation.oncancel = teardown;
  }, [isImageLayer]);

  useEffect(() => {
    if (isImageLayer) {
      return;
    }

    const pulseElement = pulseRef.current;
    if (pulseToken <= 0 || !pulseElement) {
      return;
    }

    const runId = ++pulseRunIdRef.current;
    const previousAnimation = pulseAnimationRef.current;
    if (previousAnimation) {
      previousAnimation.onfinish = null;
      previousAnimation.oncancel = null;
      previousAnimation.cancel();
    }

    pulseElement.style.opacity = '1';

    const animation = pulseElement.animate(
      [
        {
          boxShadow: 'inset 0 0 0 2px rgba(255, 255, 255, 0.9)',
          backgroundColor: 'rgba(255, 255, 255, 0.26)',
        },
        {
          boxShadow: 'inset 0 0 22px 22px rgba(255, 255, 255, 0)',
          backgroundColor: 'rgba(255, 255, 255, 0)',
        }
      ],
      {
        duration: 800,
        easing: 'ease-out'
      }
    );

    pulseAnimationRef.current = animation;

    const teardown = () => {
      if (pulseRunIdRef.current !== runId) {
        return;
      }
      if (pulseAnimationRef.current !== animation) {
        return;
      }
      pulseAnimationRef.current = null;
      pulseElement.style.opacity = '0';
    };

    animation.onfinish = teardown;
    animation.oncancel = teardown;

    return () => {
      if (pulseAnimationRef.current === animation) {
        animation.onfinish = null;
        animation.oncancel = null;
        animation.cancel();
        pulseAnimationRef.current = null;
      }
    };
  }, [isImageLayer, pulseToken]);

  useEffect(() => {
    if (!isImageLayer || pulseToken <= 0) {
      return;
    }

    triggerGhostPulse();
  }, [isImageLayer, pulseToken, triggerGhostPulse]);

  useEffect(() => {
    if (!isScissorsHoverTarget) {
      return;
    }

    triggerGhostPulse();
    const interval = window.setInterval(() => {
      triggerGhostPulse();
    }, 980);

    return () => {
      window.clearInterval(interval);
    };
  }, [isScissorsHoverTarget, triggerGhostPulse]);

  useEffect(() => {
    if (!isImageLayer || !isProcessing) {
      return;
    }

    const processingElement = processingPulseRef.current;
    if (processingElement) {
      processingPulseAnimationRef.current?.cancel();
      processingPulseAnimationRef.current = processingElement.animate(
        [
          { backgroundColor: 'rgba(70, 70, 70, 0)' },
          { backgroundColor: 'rgba(70, 70, 70, 0.35)' },
        ],
        {
          duration: 620,
          direction: 'alternate',
          iterations: Infinity,
          easing: 'ease-in-out',
        }
      );
    }

    triggerGhostPulse();
    const interval = window.setInterval(() => {
      triggerGhostPulse();
    }, 980);

    return () => {
      window.clearInterval(interval);
      processingPulseAnimationRef.current?.cancel();
      processingPulseAnimationRef.current = null;
    };
  }, [isImageLayer, isProcessing, triggerGhostPulse]);

  useEffect(() => {
    return () => {
      pulseAnimationRef.current?.cancel();
      pulseAnimationRef.current = null;
      processingPulseAnimationRef.current?.cancel();
      processingPulseAnimationRef.current = null;
      ghostAnimationRef.current?.cancel();
      ghostAnimationRef.current = null;
    };
  }, []);

  const pulseStyle = useMemo((): React.CSSProperties => ({
    position: 'absolute',
    opacity: 0,
    pointerEvents: 'none',
    zIndex: 10,
    borderRadius: 'inherit',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  }), []);

  const ghostStyle = useMemo((): React.CSSProperties => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    pointerEvents: 'none',
    zIndex: 12,
    mixBlendMode: 'screen',
    transformOrigin: 'center center',
  }), []);

  const processingPulseStyle = useMemo((): React.CSSProperties => ({
    ...pulseStyle,
    opacity: isProcessing ? 1 : 0,
    // Above the native cut preview (zIndex 35), which paints the opaque source over the layer.
    zIndex: isRasterizing ? 36 : 11,
  }), [isProcessing, isRasterizing, pulseStyle]);

  return (
    <>
      <div ref={pulseRef} style={pulseStyle} />
      {isImageLayer && <div ref={processingPulseRef} style={processingPulseStyle} />}
      {isImageLayer && !disableGhostContent && (
        <div ref={ghostRef} style={ghostStyle}>
          {ghostContent}
        </div>
      )}
    </>
  );
});

LayerFeedbackOverlays.displayName = 'LayerFeedbackOverlays';

export default LayerFeedbackOverlays;
